import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsApi } from '@/lib/api/arrex/analytics';

const VISITOR_KEY = 'seen_visitor_id';
const SESSION_ID_KEY = 'seen_session_id';
const SESSION_ACTIVITY_KEY = 'seen_session_last';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function uuid(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

let memVisitor: string | null = null;
let memSession: { id: string; last: number } | null = null;

function safeGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* storage unavailable */ }
}

function getVisitorId(): string {
  const stored = safeGet(VISITOR_KEY);
  if (stored) return stored;
  if (memVisitor) return memVisitor;
  const id = uuid();
  memVisitor = id;
  safeSet(VISITOR_KEY, id);
  return id;
}

function resolveSession(): { id: string; isNew: boolean } {
  const now = Date.now();
  const storedId = safeGet(SESSION_ID_KEY) ?? memSession?.id ?? null;
  const storedLastRaw = safeGet(SESSION_ACTIVITY_KEY);
  const storedLast = storedLastRaw ? Number(storedLastRaw) : memSession?.last ?? 0;

  const expired = !storedId || !storedLast || now - storedLast > SESSION_TIMEOUT_MS;

  if (expired) {
    const id = uuid();
    memSession = { id, last: now };
    safeSet(SESSION_ID_KEY, id);
    safeSet(SESSION_ACTIVITY_KEY, String(now));
    return { id, isNew: true };
  }

  memSession = { id: storedId!, last: now };
  safeSet(SESSION_ACTIVITY_KEY, String(now));
  return { id: storedId!, isNew: false };
}

export const VisitorTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;

    const { id: session_id } = resolveSession();
    analyticsApi.track(getVisitorId(), location.pathname, session_id);
  }, [location.pathname]);

  return null;
};
